import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Chip,
  Stack,
  Select,
  Button,
  MenuItem,
  TextField,
  InputLabel,
  Typography,
  FormControl,
  Autocomplete,
  OutlinedInput,
} from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { EMAIL_REGEX } from 'src/utils/regex';

import { getUsersByDepartment } from 'src/services/user.service';

import { scrollbarStyle } from 'src/sections/style';

export default function EditStatus({
  hideModal,
  emailsList,
  departmentList,
  canUpdate,
  status,
  editStatus,
}) {
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const [emails, setEmails] = useState(status.correspondences);
  const [emailValid, setEmailValid] = useState(true);
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const isAuthorized = canUpdate;

  const [color, setColor] = useState(
    status.statusId <= 5 ? status.statusColor : status.color || '#000000'
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    setValue,
  } = useForm({
    mode: 'all',
    defaultValues: {
      name: status.name,
      description:
        status.statusId <= 5
          ? t(`statusDescription.${status.systemMappedStatus}`)
          : status.description,
      systemMappedStatus: status.systemMappedStatus,
      departmentIds: status.allDepartmentIds || [status.departmentId],
      color: status.statusId <= 5 ? status.statusColor : status.color,
      correspondences: status.correspondences,
    },
  });

  const selectedDepts = watch('departmentIds') || [];

  const handleDepartmentChange = (event) => {
    const {
      target: { value },
    } = event;
    let newSelection = typeof value === 'string' ? value.split(',') : value;
    newSelection = newSelection.map((id) => Number(id));

    setValue('departmentIds', newSelection, { shouldValidate: true });
  };

  const handleKeyDown = (event) => {
    if ((event.key === ' ' || event.key === ',') && event.target.value !== '') {
      event.preventDefault();
      event.stopPropagation();
      handleAddEmail(event, [...emails, event.target.value]);
    }
  };

  const handleAddEmail = (event, newValue) => {
    let validEmails = true;
    newValue.forEach((email) => {
      if (EMAIL_REGEX.test(email) === false) {
        validEmails = false;
      }
    });
    if (validEmails) {
      setEmailValid(true);
      setEmails(newValue);
    } else if (newValue.length !== 0) {
      setEmailValid(false);
    }
  };

  const onSubmit = (data) => {
    const syncCommand = {
      targetIds: status.allDbIds || [status.statusId],
      departmentIds: data.departmentIds.map((id) => Number(id)),
      name: data.name,
      description: data.description,
      color,
      correspondences: emails || status.correspondences || [],
    };

    editStatus(syncCommand);
    hideModal();
  };

  useEffect(() => {
    setEmailSuggestions(emailsList);
  }, [emailsList]);

  const selectedDepartment = watch('departmentId');
  const handleHtmlColorChange = (event) => {
    setColor(event.target.value);
  };

  useEffect(() => {
    if (selectedDepartment)
      getUsersByDepartment(selectedDepartment).then((res) => {
        setEmailSuggestions(
          res.filter((user) => !user.archived).map((user) => user.email)
        );
      });
  }, [selectedDepartment]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack my={2} spacing={2.5}>
        <TextField
          fullWidth
          label={t('tableHeads.status.name')}
          disabled={status.statusId <= 5 || !canUpdate}
          {...register('name', { required: true })}
          error={!!errors.name}
          size={isSmallScreen ? 'small' : 'medium'}
        />

        <TextField
          fullWidth
          multiline
          minRows={2}
          label={t('tableHeads.status.description')}
          disabled={status.statusId <= 5 || !canUpdate}
          {...register('description', { required: true })}
          error={!!errors.description}
          size={isSmallScreen ? 'small' : 'medium'}
        />

        {isAuthorized && status.statusId > 5 && (
          <FormControl
            fullWidth
            size={isSmallScreen ? 'small' : 'medium'}
            disabled={!canUpdate}
          >
            <InputLabel id="dept-select-label">{t('attributes.department')}</InputLabel>
            <Select
              labelId="dept-select-label"
              multiple
              value={selectedDepts}
              onChange={handleDepartmentChange}
              input={<OutlinedInput label={t('attributes.department')} />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => (
                    <Chip
                      key={val}
                      label={
                        departmentList?.find((d) => Number(d.id) === Number(val))?.name
                      }
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
              MenuProps={{ PaperProps: { sx: scrollbarStyle } }}
            >
              {departmentList?.map((dep) => (
                <MenuItem key={dep.id} value={Number(dep.id)}>
                  {dep.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl fullWidth disabled size={isSmallScreen ? 'small' : 'medium'}>
          <InputLabel id="system-state-label">
            {t('tableHeads.status.systemState')}
          </InputLabel>
          <Select
            labelId="system-state-label"
            label={t('tableHeads.status.systemState')}
            value={status.systemMappedStatus}
          >
            <MenuItem value={status.systemMappedStatus}>
              {t(`status.${status.systemMappedStatus}`)}
            </MenuItem>
          </Select>
        </FormControl>

        {status.statusId > 5 && (
          <Autocomplete
            multiple
            id="email-input"
            options={emailSuggestions} // You can provide options for suggestions here
            freeSolo // Allows entering values not in the options
            defaultValue={status.correspondences}
            size={isSmallScreen ? 'small' : 'medium'}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontSize: isSmallScreen ? '0.675rem' : '1rem' }}
                  >
                    {t('tableHeads.status.correspondances')}
                  </Typography>
                }
                placeholder={t('pages.statusPage.typeEmail')}
                onKeyDown={(e) => handleKeyDown(e)}
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                error={!emailValid}
                helperText={!emailValid ? t('pages.statusPage.invalidEmail') : ''}
                InputLabelProps={{
                  sx: {
                    fontSize: isSmallScreen ? '0.675rem' : '1rem',
                  },
                }}
                inputProps={{
                  ...params.inputProps,
                  style: {
                    fontSize: isSmallScreen ? '0.675rem' : '1rem',
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    '&::placeholder': {
                      fontSize: isSmallScreen ? '0.675rem' : '1rem',
                    },
                  },
                }}
              />
            )}
            onChange={(e, newValue) => {
              handleAddEmail(e, newValue);
            }}
            clearOnBlur
            onBlur={() => setEmailValid(true)}
            value={emails}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  label={
                    <Typography
                      sx={{
                        fontSize: isSmallScreen ? '0.675rem' : '1rem',
                      }}
                    >
                      {option}
                    </Typography>
                  }
                  sx={{
                    height: isSmallScreen ? 20 : 32,
                  }}
                />
              ))
            }
            ListboxProps={{
              style: {
                fontSize: isSmallScreen && '0.675rem',
              },
            }}
          />
        )}

        {/* <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle2">{t('tableHeads.status.color')}:</Typography>
          <input
            type="color"
            value={color}
            disabled={status.statusId <= 5 || !canUpdate}
            style={{
              cursor: canUpdate && status.statusId > 5 ? 'pointer' : 'default',
              width: 40,
              height: 25,
              border: 'none',
              borderRadius: 4,
            }}
          />
        </Stack>
         */}
        <Stack direction="row" id="color-picker" alignItems="center" spacing={1}>
          <Typography variant="h6" sx={{ fontSize: isSmallScreen ? '0.675rem' : '1rem' }}>
            {t('tableHeads.status.color')} :
          </Typography>
          <input
            style={{
              cursor: 'pointer',
              width: isSmallScreen ? '40px' : {},
              height: isSmallScreen ? '22px' : {},
            }}
            type="color"
            value={color}
            onChange={handleHtmlColorChange}
            disabled={status.statusId <= 5}
          />
        </Stack>
      </Stack>

      {!canUpdate || status.statusId <= 5 ? (
        <Stack direction="row" mt={3}>
          <Button fullWidth variant="contained" color="inherit" onClick={hideModal}>
            {t('buttons.close') || 'Close'}
          </Button>
        </Stack>
      ) : (
        <Stack direction="row" spacing={2} mt={3}>
          <Button fullWidth variant="outlined" color="inherit" onClick={() => reset()}>
            {t('buttons.cancelChanges')}
          </Button>
          <LoadingButton
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
            disabled={!isValid || selectedDepts.length === 0}
          >
            {t('pages.statusPage.editStatus')}
          </LoadingButton>
        </Stack>
      )}
    </form>
  );
}

EditStatus.propTypes = {
  hideModal: PropTypes.func,
  emailsList: PropTypes.array,
  departmentList: PropTypes.array,
  canUpdate: PropTypes.bool,
  status: PropTypes.object,
  editStatus: PropTypes.func,
};
